<template>
    <div class="main-content literature-edit">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
                <h2 class="mb-1">{{ isCreating ? "New Literature" : "Edit Literature" }}</h2>
            </div>
            <router-link class="btn btn-sm btn-secondary self-start sm:self-center" :to="{ name: 'literature-portal' }">
                <span aria-hidden="true">←</span> Back to Portal
            </router-link>
        </div>
        <p class="diagonal-lines mb-4"></p>

        <div v-if="saveSuccessMessage" class="alert alert-success py-2" role="status">
            {{ saveSuccessMessage }}
        </div>
        <div v-if="saveError" class="alert alert-danger py-2" role="alert">
            {{ saveError }}
        </div>
        <div v-if="deleteError" class="alert alert-danger py-2" role="alert">
            {{ deleteError }}
        </div>

        <div v-if="loading" class="flex justify-center items-center py-12">
            <vue-loaders-ball-beat scale="2" color="black" />
        </div>

        <div v-else-if="error" class="alert alert-danger" role="alert">
            <strong>Literature could not be loaded.</strong>
            <span class="block mt-1">Return to the portal and try again.</span>
        </div>

        <form v-else-if="form" class="edit-workspace flex flex-col lg:flex-row lg:items-start" @submit.prevent="saveLiterature">
            <div class="edit-main flex-1 min-w-0">
                <div class="title-editor p-3 md:p-4">
                    <admin-form-field
                        id="literature-name"
                        v-model="form.name"
                        label="Literature name"
                        variant="title"
                        :compact="false"
                        hide-label
                        placeholder="Literature title"
                        required
                    />
                </div>

                <section class="edit-section">
                    <div class="p-3 md:p-4">
                        <admin-file-upload-field
                            id="literature-pdf-path"
                            v-model="form.path"
                            label="PDF document"
                            upload-type="pdf"
                            :preview-url="pdfUrl(form.path)"
                            preview-label="View current PDF"
                            help="No PDF attached."
                        />
                    </div>

                    <div class="grid grid-cols-1 gap-4 p-3 md:grid-cols-2 md:p-4">
                        <admin-form-field id="literature-category" v-model="form.catagory" label="Category" />
                        <admin-form-field id="literature-seo" v-model="form.seo_keyword" label="SEO keywords" />
                        <admin-form-field
                            id="literature-hubspot"
                            v-model="form.hubspot"
                            type="textarea"
                            label="HubSpot embed code"
                            :rows="4"
                            :compact="false"
                            monospace
                            class="md:col-span-2"
                        />
                    </div>
                    <div class="p-3 md:p-4">
                        <admin-form-field
                            id="literature-content"
                            v-model="form.content"
                            label="Content"
                            help="Generated automatically and cannot be edited here."
                            disabled
                        />
                    </div>
                </section>
            </div>

            <aside class="literature-sidebar w-full lg:w-64 lg:flex-none">
                <div class="sidebar-heading flex items-center justify-between px-3 py-2 font-semibold">
                    <span>Publish settings</span>
                    <span class="status-indicator" :class="`is-${form.status || 'unset'}`">
                        {{ currentStatusLabel }}
                    </span>
                </div>

                <section class="sidebar-section flex flex-col gap-3 p-3">
                    <admin-form-field id="literature-status" v-model="form.status" label="Status" :options="statusOptions" />
                    <admin-form-field
                        id="literature-start-date"
                        :value="dateInput"
                        type="date"
                        label="Start date"
                        @input="form.start_date = $event"
                    />
                    <fieldset class="mb-0">
                        <legend class="field-label mb-2">Publishing destinations</legend>
                        <div class="flex flex-col gap-1">
                            <admin-toggle-field id="literature-web-retail" v-model="form.web_retail" label="Retail" />
                            <admin-toggle-field id="literature-web-boss" v-model="form.web_boss" label="Boss" />
                            <admin-toggle-field id="literature-web-cat" v-model="form.web_cat" label="Catalog" />
                        </div>
                    </fieldset>

                    <div
                        class="sidebar-actions flex items-center gap-2 pt-1"
                        :class="isCreating ? 'justify-end' : 'justify-between'"
                    >
                        <button
                            v-if="!isCreating"
                            type="button"
                            class="btn btn-outline-danger btn-sm"
                            :disabled="saving || deleting"
                            @click="deleteDialogOpen = true"
                        >
                            Delete
                        </button>
                        <button type="submit" class="btn btn-danger btn-sm sidebar-save-button" :disabled="saving || deleting">
                            {{ submitButtonLabel }}
                        </button>
                    </div>
                </section>

                <div class="sidebar-heading px-3 py-2 font-semibold">Featured image</div>
                <section class="sidebar-section p-3">
                    <span class="field-label">Cover preview</span>
                    <div class="literature-preview-frame flex justify-center items-center">
                        <img
                            v-if="form.image_path"
                            :src="imageUrl(form.image_path)"
                            :alt="(form.name || 'Literature') + ' cover'"
                            class="lit-preview max-w-full max-h-full"
                        />
                        <span v-else class="text-xs font-semibold text-pip-gray text-center">No cover image</span>
                    </div>
                    <admin-file-upload-field
                        id="literature-image-path"
                        v-model="form.image_path"
                        label="Cover image"
                        upload-type="image"
                        :preview-url="imageUrl(form.image_path)"
                        preview-label="View current image"
                        help="No cover image attached."
                        class="mt-3"
                    />
                </section>
            </aside>
        </form>

        <admin-confirm-dialog
            id="edit-delete-literature"
            :open="deleteDialogOpen"
            title="Delete literature?"
            :message="deleteConfirmationMessage"
            confirm-label="Delete literature"
            working-label="Deleting…"
            :working="deleting"
            @confirm="deleteLiterature"
            @cancel="deleteDialogOpen = false"
        />
    </div>
</template>

<script>
import {
    AdminConfirmDialog,
    AdminFileUploadField,
    AdminFormField,
    AdminToggleField,
} from "@c/shared/admin";

export default {
    name: "LiteratureEdit",
    components: { AdminConfirmDialog, AdminFileUploadField, AdminFormField, AdminToggleField },
    data() {
        return {
            form: null,
            loading: false,
            error: null,
            saving: false,
            saveError: null,
            saveSuccessMessage: null,
            deleteDialogOpen: false,
            deleting: false,
            deleteError: null,
        };
    },
    computed: {
        isCreating() {
            return this.$route.name === "literature-create";
        },
        submitButtonLabel() {
            if (this.saving) return this.isCreating ? "Creating…" : "Saving…";
            return this.isCreating ? "Create" : "Save";
        },
        // Coerce ISO/DB timestamp into YYYY-MM-DD for <input type="date">.
        dateInput() {
            if (!this.form || !this.form.start_date) return "";
            const date = new Date(this.form.start_date);
            if (isNaN(date)) return this.form.start_date;
            return date.toISOString().slice(0, 10);
        },
        statusOptions() {
            return [
                { value: null, label: "Not set" },
                { value: "draft", label: "Draft" },
                { value: "published", label: "Published" },
                { value: "archived", label: "Archived" },
            ];
        },
        currentStatusLabel() {
            const currentStatus = this.statusOptions.find(option => option.value === this.form.status);
            return currentStatus ? currentStatus.label : "Not set";
        },
        deleteConfirmationMessage() {
            if (!this.form) return "";
            return `“${this.form.name || "Untitled literature"}” will be permanently removed. This cannot be undone.`;
        },
    },
    created() {
        if (this.isCreating) {
            this.form = this.emptyForm();
            return;
        }

        this.loading = true;
        axios
            .get(`/api/admin/literatures/${this.$route.params.id}`)
            .then(response => {
                this.form = response.data;
            })
            .catch(error => {
                this.error = error;
            })
            .finally(() => {
                this.loading = false;
            });
    },
    methods: {
        emptyForm() {
            return {
                name: "",
                path: "",
                image_path: "",
                catagory: "",
                status: "draft",
                start_date: null,
                web_retail: null,
                web_boss: null,
                web_cat: null,
                hubspot: "",
                seo_keyword: "",
                content: "",
            };
        },
        assetUrl(path, legacyDirectory) {
            if (!path) return "";
            if (/^(https?:)?\/\//.test(path) || path.charAt(0) === "/") return path;
            if (path.indexOf("media/") === 0) return `/${path}`;
            return `${legacyDirectory}${path}`;
        },
        imageUrl(path) {
            return this.assetUrl(path, "/images/catalogs/");
        },
        pdfUrl(path) {
            return this.assetUrl(path, "/docs/Literature/");
        },
        normalizedFlag(value) {
            return value === 1 || value === "1" || value === "y" || value === true ? 1 : null;
        },
        updatePayload() {
            return {
                name: this.form.name,
                path: this.form.path,
                image_path: this.form.image_path,
                catagory: this.form.catagory,
                status: this.form.status || null,
                start_date: this.dateInput || null,
                web_retail: this.normalizedFlag(this.form.web_retail),
                web_boss: this.normalizedFlag(this.form.web_boss),
                web_cat: this.normalizedFlag(this.form.web_cat),
                hubspot: this.form.hubspot,
                seo_keyword: this.form.seo_keyword,
            };
        },
        async saveLiterature() {
            if (this.saving) return;

            this.saving = true;
            this.saveError = null;
            this.saveSuccessMessage = null;

            try {
                const creating = this.isCreating;
                const response = creating
                    ? await axios.post(`/api/admin/literatures`, this.updatePayload())
                    : await axios.put(
                        `/api/admin/literatures/${this.$route.params.id}`,
                        this.updatePayload()
                    );
                this.form = response.data;
                this.saveSuccessMessage = creating
                    ? "Literature created successfully."
                    : "Literature updated successfully.";

                if (creating) {
                    await this.$router.replace({
                        name: "literature-edit",
                        params: { id: response.data.id },
                    });
                }
            } catch (error) {
                const errors = error.response && error.response.data && error.response.data.errors;
                const firstField = errors && Object.keys(errors)[0];
                this.saveError = firstField
                    ? errors[firstField][0]
                    : `Literature could not be ${this.isCreating ? "created" : "saved"}.`;
            } finally {
                this.saving = false;
            }
        },
        async deleteLiterature() {
            if (this.isCreating || this.deleting) return;

            this.deleting = true;
            this.deleteError = null;

            try {
                await axios.delete(`/api/admin/literatures/${this.$route.params.id}`);
                this.deleteDialogOpen = false;
                await this.$router.push({
                    name: "literature-portal",
                    query: { deleted: "1" },
                });
            } catch (error) {
                this.deleteDialogOpen = false;
                this.deleteError = "Literature could not be deleted. Please try again.";
            } finally {
                this.deleting = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.edit-workspace {
    border: 1px solid var(--color-light-grey);
    background: #fff;
}

.literature-sidebar {
    background: #f8f8f8;
    border-top: 1px solid var(--color-light-grey);
}

.sidebar-heading,
.edit-section-heading {
    border-bottom: 1px solid var(--color-light-grey);
    background: var(--color-gallery);
    color: var(--color-mine-shaft);
    font-size: var(--font-size-sm);
}

.sidebar-section {
    border-bottom: 1px solid var(--color-light-grey);
}

.sidebar-section:last-child {
    border-bottom: 0;
}

.literature-preview-frame {
    width: 100%;
    height: 10rem;
    padding: 0.5rem;
    border: 1px solid var(--color-light-grey);
    background: #fff;
}

.lit-preview {
    object-fit: contain;
}

.field-label {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--color-pip-gray);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-bold);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
}

legend.field-label {
    width: auto;
}

.sidebar-actions {
    border-top: 1px solid var(--color-light-grey);
}

.sidebar-save-button {
    border-radius: 4px !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.status-indicator {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    background: #e3e3e3;
    color: var(--color-pip-gray);
    font-size: 0.625rem;
    font-weight: var(--font-weight-bold);
    letter-spacing: 0.03em;
    line-height: 1.3;
    text-transform: uppercase;
}

.status-indicator.is-published {
    background: #dff1e4;
    color: #256b36;
}

.status-indicator.is-draft {
    background: #fff0c7;
    color: #755408;
}

.status-indicator.is-archived {
    background: #e6e6e6;
    color: #555;
}

.title-editor {
    background: #fff;
}

.edit-section {
    border-top: 1px solid var(--color-light-grey);
}

.min-w-0 {
    min-width: 0;
}

@media (min-width: 1024px) {
    .literature-sidebar {
        position: sticky;
        top: 5rem;
        max-height: calc(100vh - 6rem);
        overflow-y: auto;
        border-top: 0;
        border-left: 1px solid var(--color-light-grey);
        scrollbar-width: thin;
    }
}
</style>
